import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Menu, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

interface DropdownItem {
	label: string;
	onClick: () => void;
	icon?: React.ReactNode;
	disabled?: boolean;
	danger?: boolean;
	title?: string;
}

interface DropdownProps {
	trigger: React.ReactNode;
	items: DropdownItem[];
	align?: 'left' | 'right';
	className?: string;
}

export function Dropdown({
	trigger,
	items,
	align = 'left',
	className,
}: DropdownProps) {
	const colors = useThemeColors();
	return (
		<Menu as="div" className={cn('relative inline-block text-left', className)}>
			<Menu.Button as={Fragment}>{trigger}</Menu.Button>

			<Transition
				as={Fragment}
				enter="transition ease-out duration-100"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<Menu.Items
					className={cn(
						'absolute z-[100] mt-2 min-w-56 origin-top-right rounded-md shadow-lg ring-1 ring-black ring-opacity-5',
						colors.classes.focusRingNone,
						colors.classes.bgPrimary,
						align === 'right' ? 'right-0' : 'left-0',
					)}
				>
					<div className="py-1">
						{items.map((item, index) => (
							<Menu.Item key={index} disabled={item.disabled}>
								{({ active }) => (
									<button
										onClick={item.onClick}
										title={item.title}
										className={cn(
											'group flex w-full items-center whitespace-nowrap px-4 py-2 text-sm transition-colors',
											item.disabled
												? 'opacity-50 cursor-not-allowed'
												: active
													? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
													: '',
											!active &&
												(item.danger
													? colors.classes.textDanger
													: colors.classes.textPrimary),
										)}
										disabled={item.disabled}
									>
										{item.icon && (
											<Text
												className={cn(
													'mr-3',
													colors.classes.textSecondary,
													'group-hover:opacity-80',
												)}
											>
												{typeof item.icon === 'object' &&
												item.icon != null &&
												('iconName' in item.icon || 'prefix' in item.icon) ? (
													<Icon icon={item.icon as IconDefinition} />
												) : (
													item.icon
												)}
											</Text>
										)}
										{item.label}
									</button>
								)}
							</Menu.Item>
						))}
					</div>
				</Menu.Items>
			</Transition>
		</Menu>
	);
}
